import { Outlet } from "react-router";
import { useMe } from "../../hooks/useMe";
import { ForbiddenPage } from "../../routes/ForbiddenPage";
import { hasPermission } from "../../lib/permissions";
import type { AdminPermission } from "../../lib/acl/permissions";

type RequirePermissionProps = {
  permission: AdminPermission;
};

/**
 * Route guard for a surface an admin can grant to a specific user.
 *
 * Use this instead of `RequireRole` wherever the server gates on a permission
 * rather than on being an admin — gating the route more tightly than the API
 * makes the grant inert, which is indistinguishable from the feature being
 * broken for whoever was given it.
 *
 * `hasPermission` already resolves admins, so this widens access to grantees
 * without narrowing it for anyone who had it before. Fails closed: an absent or
 * still-loading account has no permissions, so a direct URL is never a way in.
 */
export const RequirePermission = ({ permission }: RequirePermissionProps) => {
  const { user } = useMe();
  if (!hasPermission(user, permission)) {
    return <ForbiddenPage />;
  }

  return <Outlet />;
};
